import React, { useState, useEffect, useCallback } from 'react';
import { Collapse, Select, Form } from 'antd';
import getSymbolFromCurrency from 'currency-symbol-map';
import gql from 'graphql-tag';
import get from 'lodash/get';
import orderBy from 'lodash/orderBy';
import { useApolloClient } from '@apollo/client';
import { queryUserGroupsByCompanyIdIndex } from 'src/_shared/api/graphql/custom/userGroups/';
import fileIcon from '../../_shared/assets/erin_lightgray.png';
import { downloadFromS3Signed } from '../../_shared/services/utils.js';
import { currencies } from './commonCurrency.js';

const { Option } = Select;

const GroupCurrencyComponent = (props) => {
	const {
		currentUser,
		onUpdateUserGroup,
		setCurrentUserGroup,
	} = props;

	const client = useApolloClient();

	// State initialization
	const [state, setState] = useState({
		defaultActiveUserGroupKey: '',
		company: get(currentUser, 'company'),
		theme: JSON.parse(get(currentUser, 'company.theme', '{}')),
		userGroup: get(currentUser, 'userGroup'),
		userGroups: [],
		errorImageSrc: null,
	});

	// Helper to update state
	const updateState = useCallback((updates) => {
		setState(prevState => ({ ...prevState, ...updates }));
	}, []);

	// Get user groups with pagination support
	const getUserGroups = useCallback(async (
		policy = 'network-only',
		nextToken = null,
		allUserGroups = []
	) => {
		try {
			const { data } = await client.query({
				query: gql(queryUserGroupsByCompanyIdIndex),
				variables: {
					companyId: get(currentUser, 'companyId'),
					after: nextToken,
				},
				fetchPolicy: policy,
			});
			
			const userGrps = [...data.queryUserGroupsByCompanyIdIndex.items];
			const token = get(data, 'queryUserGroupsByCompanyIdIndex.nextToken', null);
			allUserGroups = [...allUserGroups, ...userGrps];
			
			updateState({ userGroups: allUserGroups });
			
			if (token) {
				getUserGroups(policy, token, allUserGroups);
			}
		} catch (error) {
			console.error('Error fetching user groups:', error);
		}
	}, [client, currentUser, updateState]);

	// Initialize component
	useEffect(() => {
		const initializeComponent = async () => {
			// Fetch user groups
			getUserGroups();

			// Load error image if available
			const errorImage = get(state.company, 'errorImage.key', false);
			if (errorImage) {
				const errorImageSource = await downloadFromS3Signed(errorImage, 'erin-images');
				updateState({ errorImageSrc: errorImageSource });
			}
		};

		initializeComponent();
	}, [getUserGroups, state.company]);

	// Handle currency selection
	const handleSelectCurrency = useCallback((value, userGroupId) => {
		const { userGroup, userGroups } = state;

		// Update local state immediately for UI feedback
		const updatedUserGroups = userGroups.map(group =>
			group.id === userGroupId
				? { ...group, currency: value }
				: group
		);
		updateState({ userGroups: updatedUserGroups });

		onUpdateUserGroup({
			input: {
				id: userGroupId,
				currency: value,
			},
		});

		const currentUserGroupId = get(currentUser, 'userGroupId');
		if (currentUserGroupId === userGroupId) {
			const updatedUserGroup = {
				...userGroup,
				id: userGroup.id,
				currency: value,
			};
			setCurrentUserGroup(updatedUserGroup);
		}
	}, [state.userGroup, state.userGroups, currentUser, onUpdateUserGroup, setCurrentUserGroup, updateState]);

	// Render user groups
	const renderUserGroups = useCallback(() => {
		const { userGroups } = state;
		
		const options = Object.keys(currencies).map((key) => (
			<Option key={key} value={key}>
				{getSymbolFromCurrency(key)} {key}
			</Option>
		));

		// Sort userGroups by name
		const sortedGroups = orderBy(userGroups, ['name'], ['asc']);

		return (
			<Collapse
				className="bonus-collapse"
				bordered={false}
				expandIconPosition="end"
			>
				<Collapse.Panel
					key="currency"
					className="bonus-collapse-card"
					extra={
						<div className="bonus-collapse-icon-wrap">
							<i className="icon-arrow-down bonus-collapse-icon" />
						</div>
					}
					header={<h4>Currency</h4>}
					onClick={() => updateState({ defaultActiveUserGroupKey: 'currency' })}
				>
					{sortedGroups
						.filter((group) => group.active === true)
						.map((item) => (
							<div key={item.id} className="bs-form">
								<div className="bs-form-grp">
									<div className="custom-form-group">
										<label className="bs-label bs-label-width">
											{item.name}:
										</label>
										<Select
											style={{ width: 180 }}
											className="custom-input"
											value={item.currency || undefined}
											placeholder="Select"
											onSelect={(value) =>
												handleSelectCurrency(value, item.id)
											}
										>
											{options}
										</Select>
									</div>
								</div>
							</div>
						))}
				</Collapse.Panel>
			</Collapse>
		);
	}, [state, handleSelectCurrency, updateState]);

	// Render list with no content fallback
	const renderList = useCallback((userGroups) => {
		const { company, defaultActiveUserGroupKey, errorImageSrc } = state;
		const whiteLabel = get(company, 'whiteLabel');

		if (userGroups.length === 0) {
			return (
				<div className="no-content">
					{whiteLabel ? (
						<img
							className="no-content-icon"
							src={errorImageSrc}
							alt="error image"
						/>
					) : (
						<img alt="erin-logo" className="no-content-icon" src={fileIcon} />
					)}
					<p className="no-content-text">No User Group have been created. </p>
				</div>
			);
		}

		if (userGroups && userGroups.length > 0 && defaultActiveUserGroupKey) {
			return (
				<Collapse
					bordered={false}
					activeKey={defaultActiveUserGroupKey}
					className="bonus-collapse"
				>
					{renderUserGroups()}
				</Collapse>
			);
		}

		if (userGroups && userGroups.length > 0 && !defaultActiveUserGroupKey) {
			return (
				<Collapse bordered={false} className="bonus-collapse">
					{renderUserGroups()}
				</Collapse>
			);
		}
	}, [state, renderUserGroups]);

	// Main render
	const { userGroups } = state;
	
	return (
		<Form>
			{renderList(userGroups)}
		</Form>
	);
};

export const GroupCurrency = GroupCurrencyComponent;
